import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setZoomedMediaId } from '../../../store/livekitSlice/livekitSlice';
import './MediaContainer.css';

/**
 * MediaContainer - A reusable container for media display components
 * Handles sizing, zooming, fullscreen, and positioning logic
 * 
 * @param {React.ReactNode} children - Content to render inside the container
 * @param {string} mediaId - Unique identifier for this media (used for zoom state)
 * @param {boolean} canZoom - Enable zoom functionality (default: false)
 * @param {boolean} canFullscreen - Enable fullscreen functionality (default: false)
 * @param {boolean} canFloat - Enable floating video mode (default: false)
 * @param {'video'|'avatar'} containerType - Determines base CSS class (default: 'video')
 * @param {string} className - Additional CSS classes
 */
export default function MediaContainer({
  children,
  mediaId,
  canZoom = false,
  canFullscreen = false,
  autoZoom = false,
  canFloat = false,
  containerType = 'video',
  className = ''
}) {
  const containerRef = useRef(null);
  const { zoomedTrackId, conferenceMode, pinnedTrackId } = useSelector((state) => state.livekit);
  const {uiState} = useSelector((state) => state.activeConference);
  const dispatch = useDispatch();
  const zoomedTrackIdRef = useRef(zoomedTrackId);
  const mediaIdRef = useRef(mediaId);

  // Drag state for floating videos
  const dragData = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const pinnedTrackIdRef = useRef(pinnedTrackId);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);


  // Keep refs in sync
  useEffect(() => {
    zoomedTrackIdRef.current = zoomedTrackId;
    if (mediaId) {
      mediaIdRef.current = mediaId;
    }
  }, [zoomedTrackId, mediaId]);


  // Auto zoom if enabled and mediaId is provided, and clear on unmount if it was zoomed
  // Clear zoom state when this container unmounts if it was zoomed
  useEffect(() => {
    if (autoZoom && mediaIdRef.current && pinnedTrackIdRef.current == null) {
      dispatch(setZoomedMediaId(mediaIdRef.current));
    }
    return () => {
      if (zoomedTrackIdRef.current === mediaIdRef.current) {
        dispatch(setZoomedMediaId(null));
      }
    };
  }, [autoZoom, dispatch]);

  // Handle zoom toggle internally
  const handleZoomToggle = useCallback(() => {
    if (!canZoom || isFullscreen) return;

    const isCurrentlyZoomed = zoomedTrackIdRef.current === mediaId;
    dispatch(setZoomedMediaId(isCurrentlyZoomed ? null : mediaId));
  }, [canZoom, dispatch, mediaId, isFullscreen]);

  // Fullscreen handling
  useEffect(() => {
    if (!canFullscreen) return;

    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [canFullscreen]);

  const toggleFullscreen = useCallback(() => {
    if (!canFullscreen) return;

    const container = containerRef.current;
    if (!document.fullscreenElement) {
      container?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, [canFullscreen]);

  // Floating video positioning - only active when canFloat is true
  useEffect(() => {
    if (!canFloat || !containerRef.current) return;

    const el = containerRef.current;
    // Position initiale bottom-left
    el.style.left = "0.625rem";
    el.style.bottom = "9.0625rem";
    el.style.top = "auto";
    el.style.right = "auto";
  }, [canFloat]);

  const onPointerDown = useCallback((e) => {
    if (!canFloat) return;

    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;

    // Convertir bottom → top pour drag
    const rect = el.getBoundingClientRect();
    el.style.top = `${rect.top}px`;
    el.style.left = `${rect.left}px`;
    el.style.bottom = "auto";
    el.style.right = "auto";

    dragData.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    draggingRef.current = true;
    el.classList.add("dragging");
    el.setPointerCapture(e.pointerId);

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }, [canFloat]);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;

    const el = containerRef.current;
    if (!el) return;

    const x = e.clientX - dragData.current.x;
    const y = e.clientY - dragData.current.y;

    // Limites écran
    const maxX = window.innerWidth - el.offsetWidth;
    const maxY = window.innerHeight - el.offsetHeight;

    el.style.left = `${Math.max(0, Math.min(maxX, x))}px`;
    el.style.top = `${Math.max(0, Math.min(maxY, y))}px`;
  }, []);

  const onPointerUp = useCallback((e) => {
    const el = containerRef.current;
    if (!el) return;

    draggingRef.current = false;
    el.classList.remove("dragging");
    try {
      el.releasePointerCapture(e.pointerId);
    } catch { }
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  // Determine container CSS class based on type
  const baseClass = containerType === 'avatar' ? 'user-badge' : 'media-container';
  const isZoomed = zoomedTrackId === mediaId;

  // Build CSS classes
  const containerClasses = [
    baseClass,
    isZoomed && 'zoomed-video',
    canFloat && 'floating-video',
    isFullscreen && 'full-screen-mode',
    containerType === 'video' && 'remote-video-track-container',
    containerType === 'avatar' && 'camera-off',
    uiState.is_conference_panel_open && 'panel-open',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      onPointerDown={canFloat ? onPointerDown : undefined}
      onClick={canZoom ? handleZoomToggle : undefined}
    >
      {children}

      {/* Fullscreen button - only shown when zoomed and fullscreen is enabled */}
      {canFullscreen && isZoomed && (
        <button onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} className='zoom-video-btn'>
          {isFullscreen ? (
            <span className="material-symbols-outlined">close_fullscreen</span>
          ) : (
            <span className="material-symbols-outlined">open_in_full</span>
          )}
        </button>
      )}
    </div>
  );
}
